import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { sales_types } from "@/modules/common/constants";
import { Options } from "@/modules/common/types";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { SaleDocumentType } from "@/modules/leads/constants";

const dateSchema = z.date({
  required_error: "Required",
  invalid_type_error: "Invalid date",
});
const reserveFormSchema = z
  .object({
    unitValue: z.string().transform((val, ctx) => {
      const parsed = parseFloat(val);
      if (isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Not a number",
        });
        return z.NEVER;
      }
      return parsed;
    }),
    unitNumber: z.string().min(1, "Required"),
    areaUId: z.string().optional(),
    compoundUId: z.string().min(1, "Required"),
    dateOfReservation: dateSchema,
    expectedContractionDate: dateSchema,
    saleCategory: z.string().min(1, "Required"),
    cashPrice: z
      .string()
      .optional()
      .transform((val, ctx) => {
        if (!val) return null;
        const parsed = parseFloat(val);
        if (isNaN(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Not a number",
          });
          return z.NEVER;
        }
        return parsed;
      }),
    comment: z.string().min(4, "Minimum 4 characters"),
    organizationUId: z.string().optional(),
  })
  .refine((data) => data.dateOfReservation <= data.expectedContractionDate, {
    message:
      "Date of reservation cannot be after the expected contraction date",
    path: ["dateOfReservation"],
  });

type SaleFormData = z.infer<typeof reserveFormSchema>;

const useReservedForm = ({
  handleSaleSubmission,
  saleType,
  existingSale,
  selectedUnitLocation,
}: {
  handleSaleSubmission: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    }
  ) => void;
  saleType: string | null;
  existingSale?: any;
  selectedUnitLocation: Options | undefined;
}) => {
  const { selectedOrganization } = useOrganization();
  const [saleDocumentTypeOption, setSaleDocumentTypeOption] = useState<
    SaleDocumentType | undefined
  >(undefined);

  const { lead } = useContext(LeadPageContext);

  const current_sale_type = sales_types.find((type) => {
    return type.sale_type === saleType;
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<SaleFormData>({
    resolver: zodResolver(reserveFormSchema),
    defaultValues: {
      unitValue: undefined,
      unitNumber: "",
      areaUId: undefined,
      compoundUId: undefined,
      dateOfReservation: undefined,
      expectedContractionDate: undefined,
      saleCategory: "",
      cashPrice: undefined,
      comment: undefined,
      organizationUId: undefined,
    },
  });

  const selectedSaleCategory = watch("saleCategory");
  const selectedCompound = watch("compoundUId");

  useEffect(() => {
    if (existingSale) {
      const areaUId = existingSale?.area?.uid;

      const compoundUId = existingSale?.compound?.uid;
      reset({
        unitValue: existingSale.unitValue?.toString() || "",
        unitNumber: existingSale.unitNumber || "",
        areaUId: areaUId,
        compoundUId: compoundUId,
        dateOfReservation: existingSale.dateOfReservation
          ? new Date(existingSale.dateOfReservation)
          : undefined,
        expectedContractionDate: existingSale.expectedContractionDate
          ? new Date(existingSale.expectedContractionDate)
          : undefined,
        saleCategory: existingSale.saleCategory || "",
        cashPrice: !existingSale.cashPrice ? undefined : existingSale.cashPrice,
        comment: existingSale.comment || "",
        organizationUId: lead?.data?.lead?.organization?.uid,
      });

      setValue("areaUId", areaUId);
      setValue("compoundUId", compoundUId);
    }
  }, [existingSale, reset, setValue, lead?.data?.lead?.organization?.uid]);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };

  const onSubmit = (data: SaleFormData) => {
    //uid mapped to old lead type id = 4 nawy now
    const cashPrice =
      lead?.data?.lead_type?.lead_type === "Nawy Now" ? data.cashPrice : null;

    const formattedData = {
      areaUId: existingSale?.area?.uid || selectedUnitLocation?.optionValueUid,
      compoundUId: data.compoundUId,
      ...(cashPrice && { cashPrice: cashPrice }),
      expectedContractionDate: formatDate(data.expectedContractionDate),
      // newSale: !existingSale,
      saleCategory: data.saleCategory,
      saleTypeName: current_sale_type?.sale_type,
      comment: data.comment,
      unitNumber: data.unitNumber,
      unitValue: data.unitValue,
      ...(selectedUnitLocation && { unitLocation: selectedUnitLocation.label }),
      dateOfReservation: formatDate(data.dateOfReservation),
      ...(!existingSale && { journeyUId: lead?.data?.lead?.journey_uid }),
      ...(!existingSale && { userUId: lead?.data?.lead?.user?.id }),
      ...(!existingSale && {
        organizationUId:
          lead?.data?.lead?.organization?.uid || selectedOrganization?.uid,
      }),
      ...(cashPrice && { cashPrice: cashPrice }),
    };

    if (existingSale) {
      handleSaleSubmission(formattedData, "sale", {
        saleUId: existingSale?.uid,
        leadUId: existingSale?.lead?.uid,
      });
    } else {
      handleSaleSubmission(formattedData, "sale");
    }
  };
  return {
    control,
    handleSubmit,
    errors,
    onSubmit,
    selectedSaleCategory,
    selectedCompound,
    saleDocumentTypeOption,
    setSaleDocumentTypeOption,
  };
};

export default useReservedForm;
